import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { LogoFrame, MagnaMentesMark } from "@/components/Logos";
import NotificationBell from "@/components/NotificationBell";
import { AUTH } from "@/constants/testIds";

export function TopBar({ mode = "public" }) {
  const { user, logout } = useAuth();
  const { display_name, tagline, logo_data_url } = useBranding();
  return (
    <header className="border-b-2 border-black bg-[rgb(var(--paper))] sticky top-0 z-30 backdrop-blur">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <LogoFrame
            src={logo_data_url}
            alt={`${display_name} logo`}
            size={36}
            fallback={<MagnaMentesMark size={36} />}
          />
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight uppercase" data-testid="brand-name">
              {display_name}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/60">
              {tagline || "Field Recruitment OS"}
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-2">
          {mode === "public" && (
            <>
              <Link to="/apply" className="btn-brut btn-ghost hidden sm:inline-flex">
                Apply
              </Link>
              <Link to="/admin/login" className="btn-brut btn-ghost">
                Admin
              </Link>
            </>
          )}
          {mode === "admin" && user && user !== false && (
            <>
              <NotificationBell />
              <span className="hidden md:inline-flex pill pill-flame">
                {user.role.replace("_", " ")}
              </span>
              <span className="hidden md:inline text-sm font-mono">{user.email}</span>
              <button
                onClick={logout}
                data-testid={AUTH.logoutBtn}
                className="btn-brut btn-ghost"
              >
                Logout
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function Marquee({ items }) {
  return (
    <div className="marquee-wrap">
      <div className="marquee-track">
        {[...items, ...items].map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-3">
            <span className="w-2 h-2 bg-[rgb(var(--flame))]" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="label-brut">
        {label} {required && <span className="text-[rgb(var(--flame))]">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-black/55 mt-1 font-mono">{hint}</span>}
    </label>
  );
}

export function Section({ title, kicker, children, right }) {
  return (
    <section>
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          {kicker && (
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-black/60 mb-1">
              {kicker}
            </div>
          )}
          <h2 className="font-display text-3xl md:text-4xl">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function StatusPill({ status }) {
  const map = {
    submitted: "pill-flame",
    under_review: "pill-teal",
    approved: "pill-success",
    rejected: "pill-danger",
    on_hold: "pill-muted",
    pending: "pill-flame",
    verified: "pill-success",
  };
  const cls = map[status] || "pill-muted";
  return (
    <span className={`pill ${cls}`}>
      <span className="w-1.5 h-1.5 bg-current opacity-70" />
      {String(status).replace(/_/g, " ")}
    </span>
  );
}
